import OnboardingControls from "../../components/OnboardingControls/OnboardingControls";
import { useState, useEffect, useMemo } from "react";
import OnboardingLayout from "../../layouts/OnboardingLayout/OnboardingLayout";
import Typography from "../../components/Typography/Typography";
import RadioCheckInput from "../../components/RadioCheckInput/RadioCheckInput";
import {
  Beaker,
  Briefcase,
  Cog,
  Library,
  Stethoscope,
} from "lucide-react";

export default function Onboarding() {
  const [question, setQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isNextDisabled, setIsNextDisabled] = useState(true);

  const handleAnswer = (key, value) => {
    setAnswers((prev) => {
      const updatedAnswers = { ...prev, [key]: value };

      localStorage.setItem('onboardingAnswers', JSON.stringify(updatedAnswers));

      return updatedAnswers;
    });
  };

  useEffect(() => {
    console.log(answers);
  }, [answers]);

  const questions = useMemo(
    () => [
      {
        number: 1,
        title: "Che tipo di tesi stai scrivendo?",
        subtitle:
          "Per aiutarti a iniziare la tua tesi con gli strumenti giusti.",
        type: "radio",
        options: ["Triennale", "Magistrale", "Dottorato"],
        answerKey: "thesisType",
        hasProgress: true,
      },
      {
        number: 2,
        title: "Qual è il tuo corso di studi?",
        subtitle:
          "Per adattare il percorso di supporto alla tua area di studi.",
        type: "card",
        options: [
          {
            label: "Umanistico",
            description:
              "Come Lettere, Filosofia, Storia, Lingue, Scienze della comunicazione e affini",
            icon: (color) => <Library size={24} color={color} />, // <-- Funzione
          },
          {
            label: "Scientifico",
            description:
              "Come Matematica, Fisica, Chimica, Biologia, Informatica e discipline correlate",
            icon: (color) => <Beaker size={24} color={color} />, // <-- Funzione
          },
          {
            label: "Tecnico",
            description:
              "Come Ingegneria, Architettura, Design industriale e settori affini",
            icon: (color) => <Cog size={24} color={color} />,
          },
          {
            label: "Economico-Giuridico",
            description:
              "Come Economia, Giurisprudenza, Scienze politiche, Management e aree simili",
            icon: (color) => <Briefcase size={24} color={color} />,
          },
          {
            label: "Sanitario",
            description:
              "Come Medicina, Infermieristica, Farmacia, Professioni sanitarie e ambiti connessi",
            icon: (color) => <Stethoscope size={24} color={color} />,
          },
        ],
        answerKey: "studyField",
        hasProgress: true,
      },
      {
        number: 2.5,
        type: "testimonial",
        title: "Ci siamo passati anche noi!",
        subtitle:
          "Scrivere una tesi può sembrare un’impresa infinita… ma con Athena hai tutto il supporto che ti serve!",
        image: "/assets/images/illustrations/alphabet.png",
      },
      {
        number: 3,
        title: "Di cosa tratta la tua tesi?",
        subtitle:
          "Basta una frase, non serve che sia perfetta! Più sarai dettagliato, meglio sarà!",
        type: "text",
        answerKey: "thesisPrompt",
        hasProgress: true,
      },
      {
        number: 4,
        title: "Quanto manca alla consegna?",
        subtitle: "Per adattare il percorso ai tuoi tempi e obiettivi",
        type: "radio",
        options: ["Meno di 1 mese", "1-3 mesi", "3-6 mesi", "Più di 6 mesi"],
        answerKey: "dueRemainingTime",
        hasProgress: true,
      },
      {
        number: 4.5,
        type: "testimonial",
        title: "Hai dei dubbi? Ascolta chi ci è passato!",
        subtitle:
          "Con Athena, hai gli strumenti giusti per arrivare alla meta senza stress.",
        image: "/assets/images/illustrations/brave_secretary.png",
      },
      {
        number: 5,
        title: "C'è altro che dovremmo sapere?",
        subtitle: "È il momento di dirci tutto ciò che può esserci utile.",
        type: "radio",
        options: [
          {
            label: "Sì",
            hasTextInput: true,
            placeholder: "Scrivi qui di cosa si tratta",
          },
          {
            label: "No",
          },
        ],
        answerKey: "extraInfo",
        hasProgress: true,
      },
      {
        number: 6,
        title: "Ultima domanda! Da quale canale ci hai scoperti?",
        subtitle: "",
        type: "radio",
        options: [
          "Instagram / TikTok",
          "Consiglio di un amico",
          "Google / Motore di ricerca",
          "Annuncio online",
          "Università / Docente",
          "Altro",
        ],
        answerKey: "discoveryChannel",
        hasProgress: true,
      },
    ],
    []
  );

  const currentQuestion = questions.find((q) => q.number === question);
  const [progress, setProgress] = useState(0);
  const nonTestimonialQuestions = questions.filter(
    (q) => q.type !== "testimonial"
  );

  useEffect(() => {
    if (currentQuestion?.type !== "testimonial") {
      const currentProgress =
        ((nonTestimonialQuestions.findIndex((q) => q.number === question) + 1) /
          nonTestimonialQuestions.length) *
        100;
      setProgress(currentProgress);
    }
  }, [question, currentQuestion, nonTestimonialQuestions]);

  const currentProgress = progress;

  useEffect(() => {
    const totalQuestionsWithoutTestimonials = questions.filter(
      (q) => q.type !== "testimonial"
    ).length;

    if (!currentQuestion) {
      return;
    }

    if (question === totalQuestionsWithoutTestimonials) {
      setIsNextDisabled(!answers[currentQuestion.answerKey]);
    } else {
      setIsNextDisabled(!answers[currentQuestion.answerKey]);
    }
  }, [answers, question, currentQuestion, questions]);

  const renderQuestionContent = (current) => {
    if (!current) return null; // TODO: handle error graphically when something goes wrong rendering question, even tho it shouldn't happen

    return (
      <div className="flex flex-col justify-start items-start gap-6 md:gap-9">
        <div className="flex flex-col gap-2 md:gap-4">
          <Typography
            align="left"
            family="serif"
            size="32"
            desktopSize="48"
            weight="sb"
            color="black"
          >
            {current.title}
          </Typography>
          {current.subtitle && (
            <Typography
              align="left"
              size="16"
              desktopSize="20"
              color="gray-600"
            >
              {current.subtitle}
            </Typography>
          )}
        </div>

        {current.type === "radio" && (
          <RadioCheckInput
            options={current.options.map((option, index) => {
              if (typeof option === "string") {
                return { id: index, label: option, value: option === "true" };
              } else {
                return { id: index, ...option, value: option.value ?? option.label === "true" }; 
              }
            })}
            onChange={(selectedId) => {
              const selectedOption = current.options[selectedId];
              const selectedLabel =
                typeof selectedOption === "string" ? selectedOption : selectedOption.label;

              const selectedValue = selectedLabel === "Sì" ? true : selectedLabel === "No" ? false : selectedLabel;

              handleAnswer(current.answerKey, selectedValue);
            }}
            onTextChange={(optionId, text) => {
              handleAnswer("extraInfoText", text);
            }}
            initialSelected={
              answers[current.answerKey] !== undefined
                ? current.options.findIndex(
                  (opt) =>
                    (typeof opt === "string" ? opt : opt.label) ===
                    (answers[current.answerKey] === true
                      ? "Sì"
                      : answers[current.answerKey] === false
                        ? "No"
                        : answers[current.answerKey])
                )
                : null
            }
            multiSelect={false}
            variant="circle"
          />
        )}

        {current.type === "card" && (
          <RadioCheckInput
            options={current.options.map((option, index) => ({
              id: index,
              label: option.label,
              description: option.description,
              icon: option.icon,
            }))}
            onChange={(selectedId) =>
              handleAnswer(current.answerKey, current.options[selectedId].label)
            }
            initialSelected={
              answers[current.answerKey]
                ? current.options.findIndex(
                  (opt) => opt.label === answers[current.answerKey]
                )
                : null
            }
            multiSelect={false}
            variant="card"
          />
        )}

        {current.type === "text" && (
          <div
            className="rounded-[12px] relative w-full bg-white outline outline-1 outline-gray-200 
                     hover:outline-[3px] hover:outline-accent-light 
                     focus-within:outline-[3px] focus-within:outline-accent-light  
                     p-px overflow-hidden transition-all duration-200 ease-in-out min-h-32"
          >
            <textarea
              placeholder={
                current.placeholder || "Inserisci la tua risposta..."
              }
              value={answers[current.answerKey] || ""}
              onChange={(e) => handleAnswer(current.answerKey, e.target.value)}
              className="w-full h-full rounded-[11px] px-[16px] md:px-[18px] py-[16px] md:py-[16px] 
                       outline outline-0 focus:outline-2 focus:outline-accent text-left leading-normal 
                       resize-none overflow-auto "
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <OnboardingLayout
      hasProgress={question !== 0}
      progress={question === 0 ? 0 : currentProgress}
    >
      <div className="h-full pt-12 pb-14 md:mx-auto md:w-[40rem] w-auto px-4 flex flex-col gap-[16px] relative overflow-y-hidden">
        {question === 0 ? (
          <>
            <Typography
              align="center"
              family="serif"
              size="32"
              desktopSize="48"
              weight="sb"
              color="black"
            >
              Poche domande per un supporto su misura.
            </Typography>
            <Typography
              align="center"
              size="16"
              desktopSize="20"
              color="gray-600"
            >
              Rispondi a qualche domanda e ti guideremo passo dopo passo verso
              il successo della tua tesi.
            </Typography>
            <img
              src="/assets/images/illustrations/friday_feeling.png"
              alt="Starting Onboarding"
              className="pb-8 absolute bottom-8 md:bottom-0 left-1/2 transform -translate-x-1/2 w-[18rem] md:w-[25rem] pointer-events-none"
            />
          </>
        ) : currentQuestion?.type === "testimonial" ? (
          <>
            <Typography
              align="center"
              family="serif"
              size="32"
              desktopSize="48"
              weight="sb"
              color="black"
            >
              {currentQuestion.title}
            </Typography>
            <Typography
              align="center"
              size="16"
              desktopSize="20"
              color="gray-600"
            >
              {currentQuestion.subtitle}
            </Typography>
            <img
              src={currentQuestion.image}
              alt="Starting Onboarding"
              className="pb-8 absolute -bottom-20 left-1/2 transform -translate-x-1/2 w-[30rem] pointer-events-none"
            />
          </>
        ) : (
          <>{renderQuestionContent(currentQuestion)}</>
        )}
      </div>

      <OnboardingControls
        question={question}
        setQuestionFunction={setQuestion}
        answers={answers}
        questions={questions}
      />
    </OnboardingLayout>
  );
}
